import React, { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import {
  Plus,
  Search,
  Edit,
  Trash2,
  Upload,
  Download,
  X,
  Image as ImageIcon,
} from 'lucide-react';
import AdminLayout from '../../components/admin/AdminLayout';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '../../components/ui/dialog';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '../../components/ui/alert-dialog';
import { Badge } from '../../components/ui/badge';
import { formatCurrency } from '../../lib/currency';
import { useToast } from '../../hooks/use-toast';
import {
  getProductsPaginated,
  searchProducts,
  deleteProduct as deleteProductService,
  bulkDeleteProducts,
} from '../../services/admin/products';
import ProductForm from '../../components/admin/ProductForm';
import ProductBulkUpload from '../../components/admin/ProductBulkUpload';
import { Checkbox } from '../../components/ui/checkbox';
import useAdminAutoRefresh from '../../hooks/useAdminAutoRefresh';

const ProductsPage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { toast } = useToast();
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
  const [productToDelete, setProductToDelete] = useState(null);
  const [isBulkUploadOpen, setIsBulkUploadOpen] = useState(false);
  const [selectedProductIds, setSelectedProductIds] = useState([]);
  const [isBulkDeleteDialogOpen, setIsBulkDeleteDialogOpen] = useState(false);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [sortBy, setSortBy] = useState('createdAt');
  const [sortOrder, setSortOrder] = useState('desc');
  const fetchProducts = useCallback(
    async (targetPage = 1, reset = false, silent = false) => {
      try {
        if (!silent) setLoading(true);
        const result = await getProductsPaginated(20, targetPage, sortBy, sortOrder);
        if (reset) {
          setProducts(result.products);
        } else {
          setProducts((prev) => [...prev, ...result.products]);
        }
        setHasMore(result.hasMore);
        setPage(result.nextPage);
      } catch (error) {
        console.error('Error fetching products:', error);
        if (!silent) {
          toast({
            title: 'Hata',
            description: 'Ürünler yüklenirken bir hata oluştu.',
            variant: 'destructive',
          });
        }
      } finally {
        if (!silent) setLoading(false);
      }
    },
    [sortBy, sortOrder, toast]
  );

  useAdminAutoRefresh((silent) => fetchProducts(1, true, silent), {
    interval: 8000,
    deps: [sortBy, sortOrder],
    enabled: !isSearching && !isFormOpen,
  });

  const fetchMoreProducts = async () => {
    if (!hasMore) return;
    await fetchProducts(page, false);
  };

  const handleSearch = async () => {
    if (!searchQuery.trim()) {
      setIsSearching(false);
      setSearchResults([]);
      return;
    }

    setIsSearching(true);
    try {
      const results = await searchProducts(searchQuery);
      setSearchResults(results);
    } catch (error) {
      console.error('Error searching products:', error);
      toast({
        title: 'Hata',
        description: 'Arama yapılırken bir hata oluştu.',
        variant: 'destructive',
      });
    }
  };

  const handleCreateProduct = () => {
    setSelectedProduct(null);
    setIsFormOpen(true);
  };

  const handleEditProduct = (product) => {
    setSelectedProduct(product);
    setIsFormOpen(true);
  };

  const handleDeleteProduct = (product) => {
    setProductToDelete(product);
    setIsDeleteDialogOpen(true);
  };

  const handleToggleProduct = (productId) => {
    setSelectedProductIds((prev) =>
      prev.includes(productId) ? prev.filter((id) => id !== productId) : [...prev, productId]
    );
  };

  const handleToggleAll = () => {
    if (displayedProducts.length === 0) return;
    const allIds = displayedProducts.map((product) => product.id);
    const isAllSelected = allIds.every((id) => selectedProductIds.includes(id));
    setSelectedProductIds(isAllSelected ? [] : allIds);
  };

  const confirmDelete = async () => {
    if (!productToDelete) return;

    try {
      await deleteProductService(productToDelete.id, productToDelete.images || []);
      toast({
        title: 'Başarılı',
        description: 'Ürün başarıyla silindi.',
      });
      setIsDeleteDialogOpen(false);
      setProductToDelete(null);
      // Real-time listener will automatically update the list
    } catch (error) {
      console.error('Error deleting product:', error);
      toast({
        title: 'Hata',
        description: 'Ürün silinirken bir hata oluştu.',
        variant: 'destructive',
      });
    }
  };

  const confirmBulkDelete = async () => {
    if (selectedProductIds.length === 0) return;
    try {
      const deletedIds = await bulkDeleteProducts(selectedProductIds);
      const deletedSet = new Set(deletedIds);
      setProducts((prev) => prev.filter((product) => !deletedSet.has(product.id)));
      setSearchResults((prev) => prev.filter((product) => !deletedSet.has(product.id)));
      setSelectedProductIds([]);
      setIsBulkDeleteDialogOpen(false);
      toast({
        title: 'Başarılı',
        description: `${deletedIds.length} ürün başarıyla silindi.`,
      });
    } catch (error) {
      console.error('Error bulk deleting products:', error);
      toast({
        title: 'Hata',
        description: 'Toplu silme sırasında bir hata oluştu.',
        variant: 'destructive',
      });
    }
  };

  const handleFormSuccess = () => {
    setIsFormOpen(false);
    setSelectedProduct(null);
    // Real-time listener will automatically update the list
  };

  const displayedProducts = isSearching ? searchResults : products;
  const isAllSelected =
    displayedProducts.length > 0 &&
    displayedProducts.every((product) => selectedProductIds.includes(product.id));

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
          <div>
            <h1 className="text-3xl font-bold">Ürün Yönetimi</h1>
            <p className="text-gray-600 mt-1">Tüm ürünleri görüntüleyin, düzenleyin ve yönetin</p>
          </div>
          <div className="admin-toolbar">
            <Dialog open={isBulkUploadOpen} onOpenChange={setIsBulkUploadOpen}>
              <DialogTrigger asChild>
                <Button variant="outline">
                  <Upload size={18} className="mr-2" />
                  Toplu Yükleme
                </Button>
              </DialogTrigger>
              <DialogContent className="max-w-2xl">
                <DialogHeader>
                  <DialogTitle>Ürün Toplu Yükleme</DialogTitle>
                  <DialogDescription>
                    CSV dosyası ile ürünleri toplu olarak yükleyin
                  </DialogDescription>
                </DialogHeader>
                <ProductBulkUpload
                  onSuccess={() => {
                    setIsBulkUploadOpen(false);
                    // Real-time listener will automatically update the list
                  }}
                />
              </DialogContent>
            </Dialog>
            <Button
              variant="outline"
              onClick={() => setIsBulkDeleteDialogOpen(true)}
              disabled={selectedProductIds.length === 0}
            >
              <Trash2 size={18} className="mr-2" />
              {selectedProductIds.length > 0
                ? `${selectedProductIds.length} Ürünü Sil`
                : 'Toplu Sil'}
            </Button>
            <Button onClick={handleCreateProduct}>
              <Plus size={18} className="mr-2" />
              Yeni Ürün
            </Button>
          </div>
        </div>

        {/* Search and Filters */}
        <div className="admin-search-row flex flex-col sm:flex-row gap-4">
          <div className="flex-1 relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
            <Input
              placeholder="Ürün adı veya açıklama ile ara..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              className="pl-10"
            />
          </div>
          <Button onClick={handleSearch} variant="outline">
            Ara
          </Button>
          {isSearching && (
            <Button
              variant="ghost"
              onClick={() => {
                setSearchQuery('');
                setIsSearching(false);
                setSearchResults([]);
                fetchProducts(true);
              }}
            >
              <X size={18} className="mr-2" />
              Temizle
            </Button>
          )}
          <select
            value={`${sortBy}-${sortOrder}`}
            onChange={(e) => {
              const [field, order] = e.target.value.split('-');
              setSortBy(field);
              setSortOrder(order);
            }}
            className="w-full sm:w-auto px-4 py-2 border rounded-md bg-background"
          >
            <option value="createdAt-desc">Yeni Eklenenler</option>
            <option value="createdAt-asc">Eski Eklenenler</option>
            <option value="name-asc">İsme Göre (A-Z)</option>
            <option value="name-desc">İsme Göre (Z-A)</option>
            <option value="price-asc">Fiyata Göre (Düşük-Yüksek)</option>
            <option value="price-desc">Fiyata Göre (Yüksek-Düşük)</option>
          </select>
        </div>

        {/* Products Table */}
        {loading && products.length === 0 ? (
          <div className="text-center py-12">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-gray-900 mx-auto"></div>
            <p className="mt-4 text-gray-600">Yükleniyor...</p>
          </div>
        ) : displayedProducts.length === 0 ? (
          <div className="text-center py-12 bg-gray-50 rounded-lg">
            <p className="text-gray-600 text-lg">Ürün bulunamadı</p>
            <Button onClick={handleCreateProduct} className="mt-4">
              <Plus size={18} className="mr-2" />
              İlk Ürünü Ekle
            </Button>
          </div>
        ) : (
          <>
            <div className="bg-white rounded-lg border overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-gray-50 border-b">
                    <tr>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        <Checkbox checked={isAllSelected} onCheckedChange={handleToggleAll} />
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Görsel
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Ürün Adı
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Kategori
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Fiyat
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Stok
                      </th>
                      <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                        Durum
                      </th>
                      <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                        İşlemler
                      </th>
                    </tr>
                  </thead>
                  <tbody className="bg-white divide-y divide-gray-200">
                    {displayedProducts.map((product) => (
                      <tr key={product.id} className="hover:bg-gray-50">
                        <td className="px-6 py-4 whitespace-nowrap">
                          <Checkbox
                            checked={selectedProductIds.includes(product.id)}
                            onCheckedChange={() => handleToggleProduct(product.id)}
                          />
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          {product.images && product.images.length > 0 ? (
                            <img
                              src={product.images[0]}
                              alt={product.name}
                              className="h-16 w-16 object-cover rounded"
                            />
                          ) : (
                            <div className="h-16 w-16 bg-gray-100 rounded flex items-center justify-center">
                              <ImageIcon className="text-gray-400" size={24} />
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4">
                          <div className="text-sm font-medium text-gray-900">{product.name}</div>
                          {product.description && (
                            <div className="text-sm text-gray-500 line-clamp-1">
                              {product.description.replace(/<[^>]*>/g, '').substring(0, 50)}...
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {product.category}
                          {product.subcategory && ` / ${product.subcategory}`}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                          {formatCurrency(product.price || 0)}
                          {product.originalPrice && (
                            <div className="text-xs text-gray-500 line-through">
                              {formatCurrency(product.originalPrice)}
                            </div>
                          )}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                          {product.stock || 0}
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                          <div className="flex gap-2">
                            {product.onSale && (
                              <Badge variant="destructive">İndirimde</Badge>
                            )}
                            {product.isNew && (
                              <Badge variant="default">Yeni</Badge>
                            )}
                            {product.isBestSeller && (
                              <Badge variant="secondary">Çok Satan</Badge>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                          <div className="flex justify-end gap-2">
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleEditProduct(product)}
                            >
                              <Edit size={16} />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteProduct(product)}
                              className="text-red-600 hover:text-red-700"
                            >
                              <Trash2 size={16} />
                            </Button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Load More */}
            {!isSearching && hasMore && (
              <div className="text-center">
                <Button variant="outline" onClick={fetchMoreProducts}>
                  Daha Fazla Yükle
                </Button>
              </div>
            )}
          </>
        )}

        {/* Product Form Dialog */}
        <Dialog open={isFormOpen} onOpenChange={setIsFormOpen}>
          <DialogContent className="max-w-4xl max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>
                {selectedProduct ? 'Ürünü Düzenle' : 'Yeni Ürün Ekle'}
              </DialogTitle>
              <DialogDescription>
                {selectedProduct
                  ? 'Ürün bilgilerini güncelleyin'
                  : 'Yeni ürün eklemek için tüm bilgileri doldurun'}
              </DialogDescription>
            </DialogHeader>
            <ProductForm
              product={selectedProduct}
              onSuccess={handleFormSuccess}
              onCancel={() => setIsFormOpen(false)}
            />
          </DialogContent>
        </Dialog>

        {/* Delete Confirmation Dialog */}
        <AlertDialog open={isDeleteDialogOpen} onOpenChange={setIsDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Ürünü Sil</AlertDialogTitle>
              <AlertDialogDescription>
                {productToDelete && (
                  <>
                    "{productToDelete.name}" ürününü silmek istediğinizden emin misiniz?
                    Bu işlem geri alınamaz.
                  </>
                )}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>İptal</AlertDialogCancel>
              <AlertDialogAction onClick={confirmDelete} className="bg-red-600 hover:bg-red-700">
                Sil
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Bulk Delete Confirmation Dialog */}
        <AlertDialog open={isBulkDeleteDialogOpen} onOpenChange={setIsBulkDeleteDialogOpen}>
          <AlertDialogContent>
            <AlertDialogHeader>
              <AlertDialogTitle>Seçili Ürünleri Sil</AlertDialogTitle>
              <AlertDialogDescription>
                {selectedProductIds.length} ürünü silmek istediğinizden emin misiniz?
                Bu işlem geri alınamaz.
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>İptal</AlertDialogCancel>
              <AlertDialogAction onClick={confirmBulkDelete} className="bg-red-600 hover:bg-red-700">
                Sil
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>
      </div>
    </AdminLayout>
  );
};

export default ProductsPage;
